import {
    ColoursObj,
    Gutters,
    MenuButton,
    StyledPullToRefresh,
    Typography,
} from '@eventr/common-storybook'
import { Box, Stack } from '@mui/material'
import { useEffect, useMemo } from 'react'
import { useShallow } from 'zustand/react/shallow'
import i18n from '../../../i18n/i18next'
import { bs, useBoundStore } from '../../../state/bound.store'
import { openBrowser } from '../../../utils'
import { useDeviceInfo } from '../../appState/deviceInfo'
import { useGlobalRouter } from '../../globalRouter/useGlobalRouter'
import { useGuestStore } from '../../guest/guest.store'
import { NavigationView } from '../../layout/NavigationView'
import { AccountAvatarStack } from './accountAvatarStack'

export const AccountMain = () => {
    const { pushPath } = useGlobalRouter()
    const { version } = useDeviceInfo(
        useShallow((s) => {
            return { version: s.version }
        })
    )
    const {
        override,
        itineraryMap,
        setOverride,
        wpUser,
        profile,
        selectedOrg,
        accessRequests,
        profileRequired,
    } = useBoundStore(
        useShallow((s) => {
            return {
                override: s.override,
                setOverride: s.setOverride,
                wpUser: s.wpUser,
                itineraryMap: s.itineraryMap,
                profile: s.profile,
                selectedOrg: s.selectedOrg,
                accessRequests: s.accessRequests,
                profileRequired: s.profileRequired,
            }
        })
    )

    useEffect(() => {
        bs().fetchUsers()
        if (override !== null) {
            setOverride(null)
        }
    }, [override, setOverride])

    const showAccessRequests = useMemo(() => {
        return wpUser?.roles?.some((role) => {
            return ['administrator', 'eventr_manager', 'owner'].includes(role)
        })
    }, [wpUser])

    const getJustBuildNumber = (version: string) => {
        if (version === 'webView' || !version) return '0'
        return version.split('.')[3].split('-')[0]
    }

    const footer = () => {
        return (
            <Stack
                alignItems="center"
                justifyContent="flex-end"
                gap="16px"
                paddingBottom="16px"
            >
                <div
                    onClick={() => {
                        profile
                            ? bs().logout()
                            : pushPath('/authentication/signin')
                    }}
                >
                    <Typography
                        variant="bodyRegular"
                        color={ColoursObj.core.secondary}
                        id="logOutButton"
                    >
                        {profile
                            ? i18n.t('account.logout')
                            : i18n.t('authentication.sign-in.sign-in')}
                    </Typography>
                </div>

                <Stack>
                    <Typography
                        color={ColoursObj.shades.shade2}
                        textAlign="center"
                        variant="bodySubtext"
                    >
                        V
                        {version.buildVersion +
                            '.' +
                            (getJustBuildNumber(version.reactVersion ?? '') ??
                                '0')}
                    </Typography>
                    <Typography
                        color={ColoursObj.shades.shade2}
                        textAlign="center"
                        variant="bodySubtext"
                    >
                        © Copyright Soft Pauer{' '}
                        {new Date().getUTCFullYear().toString()}
                    </Typography>
                </Stack>
            </Stack>
        )
    }

    return (
        <NavigationView
            navigationBarTitle={i18n.t('account.account')}
            footer={!profile ? footer() : null}
            isRoot
        >
            <StyledPullToRefresh
                onRefresh={async () => {
                    /// Nothing
                }}
                promiseFunction={async () => {
                    await bs().getProfile()
                }}
                maxPullDownDistance={80}
            >
                <Gutters>
                    <Stack gap="16px">
                        {profile && (
                            <AccountAvatarStack
                                count={profileRequired?.length ?? 0}
                            />
                        )}

                        <Stack direction="column" alignItems="center" gap="8px">
                            {profile && (
                                <>
                                    <MenuButton
                                        id="swapTeamMenuButton"
                                        onClick={() => {
                                            pushPath('/account/changeOrg')
                                        }}
                                        endIcon={'Chevron_Right'}
                                        endIconColour={ColoursObj.font.primary}
                                        style={{
                                            height: '76px',
                                            background:
                                                ColoursObj.backgrounds
                                                    .secondary,
                                        }}
                                    >
                                        <Stack
                                            alignItems="flex-start"
                                            gap={'4px'}
                                        >
                                            <Typography
                                                variant="bodyRegular"
                                                id="swapTeamButton"
                                                color={ColoursObj.font.primary}
                                            >
                                                {i18n.t('account.swapTeam')}
                                            </Typography>
                                            {selectedOrg &&
                                                selectedOrg.friendlyName && (
                                                    <Typography
                                                        id="selectedTeamName"
                                                        variant="bodySubtext"
                                                        color={
                                                            ColoursObj.font
                                                                .secondary
                                                        }
                                                    >
                                                        {selectedOrg.friendlyName ||
                                                            selectedOrg.name}
                                                    </Typography>
                                                )}
                                        </Stack>
                                    </MenuButton>
                                    {!useGuestStore.getState().isGuestMode &&
                                        selectedOrg &&
                                        wpUser && (
                                            <MenuButton
                                                id="itinerariesMenuButton"
                                                onClick={() => {
                                                    pushPath(
                                                        '/account/itineraries'
                                                    )
                                                }}
                                                endIcon={'Chevron_Right'}
                                                endIconColour={
                                                    ColoursObj.font.primary
                                                }
                                                style={{
                                                    background:
                                                        ColoursObj.backgrounds
                                                            .secondary,
                                                }}
                                                counter={
                                                    itineraryMap?.length ?? null
                                                }
                                            >
                                                <Typography
                                                    variant="bodyRegular"
                                                    color={
                                                        ColoursObj.font.primary
                                                    }
                                                >
                                                    {i18n.t(
                                                        'account.itineraries'
                                                    )}
                                                </Typography>
                                            </MenuButton>
                                        )}
                                    {showAccessRequests && (
                                        <MenuButton
                                            id="accessRequestsMenuButton"
                                            onClick={() => {
                                                pushPath(
                                                    '/account/accessRequests'
                                                )
                                            }}
                                            endIcon={'Chevron_Right'}
                                            style={{
                                                background:
                                                    ColoursObj.backgrounds
                                                        .secondary,
                                            }}
                                            endIconColour={
                                                ColoursObj.font.primary
                                            }
                                            counter={
                                                accessRequests?.length ?? 0
                                            }
                                        >
                                            <Box
                                                display={'grid'}
                                                gridTemplateColumns={'1fr auto'}
                                                justifyItems={'start'}
                                            >
                                                <Typography
                                                    variant="bodyRegular"
                                                    color={
                                                        ColoursObj.font.primary
                                                    }
                                                >
                                                    {i18n.t(
                                                        'account.access-requests'
                                                    )}
                                                </Typography>
                                            </Box>
                                        </MenuButton>
                                    )}
                                    <MenuButton
                                        id="settingsMenuButton"
                                        onClick={() => {
                                            pushPath('/account/settings')
                                        }}
                                        endIcon={'Chevron_Right'}
                                        endIconColour={ColoursObj.font.primary}
                                        style={{
                                            background:
                                                ColoursObj.backgrounds
                                                    .secondary,
                                        }}
                                    >
                                        <Typography
                                            variant="bodyRegular"
                                            color={ColoursObj.font.primary}
                                        >
                                            {i18n.t('account.settings')}
                                        </Typography>
                                    </MenuButton>
                                </>
                            )}

                            <MenuButton
                                id="linkToTutorials"
                                onClick={() => {
                                    openBrowser('https://l.softpauer.com/lw') /// Tutorials link
                                }}
                                endIcon={'Leave_App'}
                                endIconColour={ColoursObj.font.primary}
                                style={{
                                    background:
                                        ColoursObj.backgrounds.secondary,
                                }}
                            >
                                <Typography
                                    variant="bodyRegular"
                                    color={ColoursObj.font.primary}
                                >
                                    {i18n.t('account.tutorials')}
                                </Typography>
                            </MenuButton>
                            <MenuButton
                                id="linkToPublicItineraries"
                                onClick={() => {
                                    openBrowser(
                                        'https://eventr.softpauer.com/upcoming-events/'
                                    )
                                }}
                                endIcon={'Leave_App'}
                                endIconColour={ColoursObj.font.primary}
                                style={{
                                    background:
                                        ColoursObj.backgrounds.secondary,
                                }}
                            >
                                <Typography
                                    variant="bodyRegular"
                                    color={ColoursObj.font.primary}
                                    style={{
                                        // This Menu button has long text that on small phones overflows 2 lines, use display block to keep left align
                                        textAlign: 'left',
                                        display: 'block',
                                    }}
                                >
                                    {i18n.t('account.publicEvents')}
                                </Typography>
                            </MenuButton>
                        </Stack>
                        {profile && footer()}
                    </Stack>
                </Gutters>
            </StyledPullToRefresh>
        </NavigationView>
    )
}
